import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { blockMasterApi, BlockRow, coldstoreMasterApi, ColdstoreRow, floorMasterApi, FloorRow, tenantMasterApi, TenantRow } from "./api";

const AddModal: React.FC<{ title: string; fields: { key: string; label: string }[]; onClose: () => void;
  onSubmit: (v: Record<string, string>) => Promise<{ Error: string | null }>; testId: string }> = ({ title, fields, onClose, onSubmit, testId }) => {
  const [v, setV] = useState<Record<string, string>>({}); const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { const r = await onSubmit(v); if (r.Error) setErr(r.Error); else onClose(); }
    catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={title} onClose={onClose} testId={testId}>
      <form onSubmit={submit} data-testid={`${testId}-form`}>
        {fields.map((f) => (
          <div className="mb-3" key={f.key}><label className="form-label">{f.label}</label>
            <input className="form-control" value={v[f.key] || ""} onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`${testId}-${f.key}`} /></div>
        ))}
        {err && <div className="alert alert-danger" data-testid={`${testId}-error`}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid={`${testId}-submit`}>{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of ApiV2/ColdStore/Controller/ColdstoresController.php - table CsStores. */
export function ColdstoresMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["coldstore", "masters", "coldstores"], queryFn: () => coldstoreMasterApi.list() });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Coldstores" location="ColdStore" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-coldstore">+ New Coldstore</button></div>
        <ErpTable<ColdstoreRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : null} testId="coldstores-table"
          columns={[{ key: "SNo", title: "S.No" }, { key: "StoreName", title: "Store Name" }, { key: "StoreLocation", title: "Location" },
            { key: "StoreBlocks", title: "Blocks" }, { key: "StoreStatus", title: "Status", render: (r) => r.StoreStatus === "1" ? "Active" : "Inactive" }]} />
      </div></div>
      {open && <AddModal title="New Coldstore" testId="coldstore-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["coldstore", "masters", "coldstores"] }); }}
        fields={[{ key: "StoreName", label: "Store Name" }, { key: "StoreLocation", label: "Location" }, { key: "StoreAddress", label: "Address" },
          { key: "Incharge", label: "Incharge" }, { key: "StorePhoneNumber", label: "Phone" }, { key: "StoreStatus", label: "Status (1=Active)" }]}
        onSubmit={(v) => coldstoreMasterApi.post(v)} />}
    </>
  );
}

/** Port of ApiV2/ColdStore/Controller/StoreTenantsController.php - table CsTenant ("Stock Belongs To"). */
export function TenantsMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["coldstore", "masters", "tenants"], queryFn: () => tenantMasterApi.list() });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Tenants (Stock Belongs To)" location="ColdStore" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-tenant">+ New Tenant</button></div>
        <ErpTable<TenantRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : null} testId="tenants-table"
          columns={[{ key: "SNo", title: "S.No" }, { key: "TenantName", title: "Tenant Name" }, { key: "TenantAddress", title: "Address" },
            { key: "TenantStatus", title: "Status", render: (r) => r.TenantStatus === "1" ? "Active" : "Inactive" }]} />
      </div></div>
      {open && <AddModal title="New Tenant" testId="tenant-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["coldstore", "masters", "tenants"] }); }}
        fields={[{ key: "TenantName", label: "Tenant Name" }, { key: "TenantAddress", label: "Address" }, { key: "TenantStatus", label: "Status (1=Active)" }]}
        onSubmit={(v) => tenantMasterApi.post(v)} />}
    </>
  );
}

/** Port of ApiV2/ColdStore/Controller/BlockDataController.php - table CsStoresBlockData. */
export function BlocksMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["coldstore", "masters", "blocks"], queryFn: () => blockMasterApi.list() });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Blocks" location="ColdStore" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-block">+ New Block</button></div>
        <ErpTable<BlockRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : null} testId="blocks-table"
          columns={[{ key: "SNo", title: "S.No" }, { key: "BlockName", title: "Block Name" }, { key: "FloorName", title: "Floors" },
            { key: "BlockStatus", title: "Status", render: (r) => r.BlockStatus === "1" ? "Active" : "Inactive" }]} />
      </div></div>
      {open && <AddModal title="New Block" testId="block-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["coldstore", "masters", "blocks"] }); }}
        fields={[{ key: "BlockName", label: "Block Name" }, { key: "Floor", label: "Floor IDs (comma-separated)" }, { key: "Status", label: "Status (1=Active)" }]}
        onSubmit={(v) => blockMasterApi.post(v)} />}
    </>
  );
}

/** Port of ApiV2/ColdStore/Controller/FloorDataController.php - table CsStoresFloorData. */
export function FloorsMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["coldstore", "masters", "floors"], queryFn: () => floorMasterApi.list() });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Floors" location="ColdStore" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-floor">+ New Floor</button></div>
        <ErpTable<FloorRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : null} testId="floors-table"
          columns={[{ key: "SNo", title: "S.No" }, { key: "FloorName", title: "Floor Name" }, { key: "ProcessingStationID", title: "Plant" },
            { key: "Status", title: "Status", render: (r) => r.Status === "1" ? "Active" : "Inactive" }]} />
      </div></div>
      {open && <AddModal title="New Floor" testId="floor-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["coldstore", "masters", "floors"] }); }}
        fields={[{ key: "FloorName", label: "Floor Name" }, { key: "ProcessingStationID", label: "Plant ID" }, { key: "Status", label: "Status (1=Active)" }]}
        onSubmit={(v) => floorMasterApi.post(v)} />}
    </>
  );
}
